'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const FORM_SERVICES = [
  { title: 'Website', subtitle: 'Visibility & Conversion' },
  { title: 'Web Application', subtitle: 'Custom SaaS & Platforms' },
  { title: 'Framer Development', subtitle: 'No-Code Marketing Sites' },
  { title: 'SEO Optimization', subtitle: 'Technical SEO & Content' },
  { title: 'AI Integration', subtitle: 'Automation & Intelligence' },
]

const BUDGET_OPTIONS = ['Under $5K', '$5K – $15K', '$15K – $50K', '$50K+']
const TIMELINE_OPTIONS = ['ASAP', '1 – 2 months', '3 – 6 months', 'Not sure yet']

interface ContactFormProps {
  className?: string
}

export default function ContactForm({ className = '' }: ContactFormProps) {
  const [step, setStep] = useState(0)
  const [service, setService] = useState('')
  const [budget, setBudget] = useState('')
  const [timeline, setTimeline] = useState('')
  const [details, setDetails] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const totalSteps = 4

  const stepVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service, budget, timeline, details, name, email, company }),
      })
    } catch {
      // fail silently — show thank you regardless
    }
    setSubmitting(false)
    setStep(3)
  }

  return (
    <div className={`bg-[#eeebf3] px-8 md:px-14 py-16 md:py-20 flex flex-col ${className}`} style={{ minHeight: '580px' }}>
      {/* Progress bar */}
      <div className="flex gap-2 mb-12">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? 'bg-brand-500' : 'bg-white'
            }`}
          />
        ))}
      </div>

      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step-0"

              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col h-full"
            >
              <h3 className="text-2xl md:text-3xl font-semibold text-charcoal-800 mb-2">What&apos;s your project?</h3>
              <p className="text-charcoal-500 mb-8">Select a category to get started.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
                {FORM_SERVICES.map((s) => (
                  <button
                    key={s.title}
                    type="button"
                    onClick={() => { setService(s.title); setStep(1) }}
                    className={`rounded-2xl border text-center px-4 py-8 transition-all duration-200 hover:border-brand-500 hover:shadow-md ${
                      service === s.title ? 'border-brand-500 bg-brand-50' : 'border-charcoal-100 bg-white'
                    }`}
                  >
                    <span className="block text-lg font-semibold text-charcoal-800">{s.title}</span>
                    <span className="block text-sm text-charcoal-500 mt-1">{s.subtitle}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-1"

              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col h-full"
            >
              <h3 className="text-2xl md:text-3xl font-semibold text-charcoal-800 mb-2">Tell us more</h3>
              <p className="text-charcoal-500 mb-8">Help us understand the scope.</p>
              <div className="space-y-6 flex-1">
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Budget range</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {BUDGET_OPTIONS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setBudget(b)}
                        className={`rounded-xl border px-4 py-3 text-sm transition-all duration-200 hover:border-brand-500 ${
                          budget === b ? 'border-brand-500 bg-brand-50 text-brand-600' : 'border-charcoal-100 bg-white text-charcoal-600'
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Timeline</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TIMELINE_OPTIONS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTimeline(t)}
                        className={`rounded-xl border px-4 py-3 text-sm transition-all duration-200 hover:border-brand-500 ${
                          timeline === t ? 'border-brand-500 bg-brand-50 text-brand-600' : 'border-charcoal-100 bg-white text-charcoal-600'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Brief description</label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Tell us about your project..."
                    rows={3}
                    className="w-full rounded-xl border border-charcoal-100 px-4 py-3 text-sm text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:border-brand-500 transition-colors resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={() => setStep(0)} className="px-6 py-3 text-sm text-charcoal-400 hover:text-charcoal-600 transition-colors">Back</button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!budget || !timeline}
                  className="flex-1 px-6 py-3 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-2"

              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col h-full"
            >
              <h3 className="text-2xl md:text-3xl font-semibold text-charcoal-800 mb-2">How can we reach you?</h3>
              <p className="text-charcoal-500 mb-8">We&apos;ll get back to you within 24 hours.</p>
              <div className="space-y-5 flex-1">
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-xl border border-charcoal-100 px-4 py-3 text-sm text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-xl border border-charcoal-100 px-4 py-3 text-sm text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-600 mb-2">Company <span className="text-charcoal-300">(optional)</span></label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Your company"
                    className="w-full rounded-xl border border-charcoal-100 px-4 py-3 text-sm text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:border-brand-500 transition-colors"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={() => setStep(1)} className="px-6 py-3 text-sm text-charcoal-400 hover:text-charcoal-600 transition-colors">Back</button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!name || !email || submitting}
                  className="flex-1 px-6 py-3 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step-3"

              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="flex-1 flex flex-col items-center justify-center text-center h-full"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center mb-6"
              >
                <svg className="w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <motion.path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.5, duration: 0.4, ease: 'easeOut' }}
                  />
                </svg>
              </motion.div>
              <h3 className="text-2xl font-semibold text-charcoal-800 mb-2">Thank you!</h3>
              <p className="text-charcoal-400 max-w-sm">We&apos;ve received your inquiry and will get back to you within 24 hours.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
